import { OrgChartTreeNode } from './types';

// The org chart's tree engine. Pure: the host keeps the whole tree in state and updates it with
// getInitialExpansion / toggleNode / changePage / revealNode; OrgChart turns it into the drawn rows with
// buildChartRows.

// Expansion and page of a freshly built node: everything above the Display Levels window starts expanded on
// its first page, and the root always does.
export function getInitialExpansion(level: number, hasChildren: boolean, displayLevel: number) {
  return {
    isExpanded: (level < displayLevel && hasChildren) || level === 0,
    childrenPage: level === 0 || level < displayLevel ? 1 : 0,
  };
}

// A load-more node takes the first child's data so the host's renderer has a full row, minus that child's
// own subtree and match flag.
function loadMoreNode<T extends OrgChartTreeNode<T>>(
  parent: T,
  firstChild: T,
  nodeType: 'loadNext' | 'loadPrevious',
  numChildren: number,
): T {
  return {
    ...firstChild,
    isExpanded: false,
    childrenPage: 0,
    linked: false,
    isMatch: false,
    id: `${parent.id}-load-${nodeType === 'loadNext' ? 'next' : 'previous'}`,
    nodeType,
    numChildren,
    children: [],
  };
}

export type ChartRowsOptions<T> = {
  // "Show up to N": how many of an expanded node's children are drawn per page.
  pageSize: number;
  // "Display N Levels": how many of the deepest levels are drawn.
  displayLevel: number;
  // Keeps a node's children whole instead of paging them (e.g. a level the host pages on the server).
  keepChildrenWhole?: (node: T) => boolean;
  // Host-built nodes drawn before / after an expanded node's children (e.g. server-paging buttons).
  getExtraChildren?: (node: T) => { before: T[]; after: T[] } | undefined;
};

// Prunes collapsed subtrees and cuts each expanded node's children to its current page, with loadPrevious /
// loadNext nodes and the host's extra children at the ends.
function paginateTree<T extends OrgChartTreeNode<T>>(node: T, options: ChartRowsOptions<T>): T {
  if (!node.isExpanded) {
    return { ...node, children: [] };
  }

  const { pageSize, keepChildrenWhole, getExtraChildren } = options;
  const children = node.children ?? [];
  const pageChild = (child: T) => paginateTree(child, options);
  const extraChildren = getExtraChildren?.(node);
  let displayedChildren: T[];

  if (keepChildrenWhole?.(node)) {
    displayedChildren = children.map(pageChild);
  } else {
    const startIndex = (node.childrenPage - 1) * pageSize;
    const endIndex = node.childrenPage * pageSize;
    const [firstChild] = children;

    displayedChildren = children.slice(startIndex, endIndex).map(pageChild);

    if (firstChild && node.childrenPage > 1) {
      displayedChildren.unshift(loadMoreNode(node, firstChild, 'loadPrevious', pageSize));
    }

    if (firstChild && children.length > endIndex) {
      displayedChildren.push(loadMoreNode(node, firstChild, 'loadNext', children.length - endIndex));
    }
  }

  return {
    ...node,
    children: [...(extraChildren?.before ?? []), ...displayedChildren, ...(extraChildren?.after ?? [])],
  };
}

// Every node, depth-first, linked by parentId rather than nesting.
function flattenTree<T extends OrgChartTreeNode<T>>(node: T): T[] {
  return [{ ...node, children: [] }, ...(node.children ?? []).flatMap((child) => flattenTree(child))];
}

// The Display Levels window: only the deepest `displayLevel` levels drawn, plus the root and every match.
// Above the window an expanded node becomes a "…" (prevTree) node and a collapsed one is dropped — a
// match is drawn whole either way, so a host's filter never hides what it found.
function limitToDisplayLevels<T extends OrgChartTreeNode<T>>(nodes: T[], displayLevel: number): T[] {
  const maxLevel = Math.max(...nodes.map((node) => node.level));

  return nodes.flatMap((node) => {
    if (node.level === 0 || node.isMatch || node.level > maxLevel - displayLevel) {
      return [node];
    }

    if (node.isExpanded) {
      return [{ ...node, nodeType: 'prevTree' }];
    }

    return [];
  });
}

// The rows d3-org-chart draws for the host's tree.
export function buildChartRows<T extends OrgChartTreeNode<T>>(tree: T, options: ChartRowsOptions<T>): T[] {
  return limitToDisplayLevels(flattenTree(paginateTree(tree, options)), options.displayLevel);
}

// Expands or collapses the node `id` at `level` (toggles unless `isExpanded` is given) and closes every
// other branch at that level or below that isn't on its path; `ancestorIDs` is that path from the root.
export function toggleNode<T extends OrgChartTreeNode<T>>(
  node: T,
  target: { id: string; level: number; ancestorIDs: string[]; isExpanded?: boolean },
): T {
  const children = node.children?.map((child) => toggleNode(child, target));

  if (node.level === target.level) {
    const isTargetNode = node.id === target.id;
    const isExpanded = isTargetNode ? (target.isExpanded ?? !node.isExpanded) : false;

    return { ...node, isExpanded, childrenPage: isExpanded ? 1 : 0, children };
  }

  const isOffPath = node.level > target.level || !target.ancestorIDs.includes(node.id);

  return {
    ...node,
    isExpanded: isOffPath ? false : node.isExpanded,
    childrenPage: node.level > target.level ? 0 : node.childrenPage,
    children,
  };
}

// Moves the expanded node `parentID` to its next or previous page of children.
export function changePage<T extends OrgChartTreeNode<T>>(node: T, parentID: string, direction: 'next' | 'prev'): T {
  if (node.id === parentID && node.isExpanded) {
    return { ...node, childrenPage: node.childrenPage + (direction === 'next' ? 1 : -1) };
  }

  return { ...node, children: node.children?.map((child) => changePage(child, parentID, direction)) };
}

function revealInSubtree<T extends OrgChartTreeNode<T>>(node: T, targetID: string, pageSize: number): [T, boolean] {
  if (!node.children?.length) {
    return [node, node.id === targetID];
  }

  let foundIndex = -1;

  const children = node.children.map((child, index) => {
    const [updatedChild, found] = revealInSubtree(child, targetID, pageSize);

    if (found && foundIndex === -1) {
      foundIndex = index;
    }

    return updatedChild;
  });

  const childrenPage = foundIndex === -1 ? node.childrenPage : Math.floor(foundIndex / pageSize) + 1;

  return [{ ...node, childrenPage, children }, foundIndex !== -1 || node.id === targetID];
}

// Turns every ancestor of `targetID` to the page that holds the branch leading to it. Doesn't expand anything.
export function revealNode<T extends OrgChartTreeNode<T>>(node: T, targetID: string, pageSize: number): T {
  return revealInSubtree(node, targetID, pageSize)[0];
}
